import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { formatBC } from "@/lib/format";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Share2, Copy, Check, TrendingUp, ExternalLink, Sparkles, Package,
  ShoppingCart, Wallet, MousePointerClick,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Operator's Affiliate Dashboard
// - Shows their permanent affiliate code, share-link builder for any product,
//   and lifetime + pending commissions with a per-product breakdown.
// -----------------------------------------------------------------------------
export default function Affiliate() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/affiliate/my-stats"),
        api.get("/products"),  // authed catalog view
      ]);
      setStats(s.data);
      setProducts(p.data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const publicBase = useMemo(() => window.location.origin, []);
  const buildLink = (productId) => `${publicBase}/p/${productId}?ref=${stats?.code}`;

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 1500);
    } catch { toast.error("Clipboard blocked. Copy manually below."); }
  };

  const shareTargets = (link, product) => {
    const encodedUrl = encodeURIComponent(link);
    const msg = encodeURIComponent(
      `Check out ${product?.name || "this product"} on Zenith Flow — I earn a small commission when you buy through my link. Thanks! ${link}`
    );
    return [
      { label: "WhatsApp", href: `https://wa.me/?text=${msg}`, testid: "share-whatsapp" },
      { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, testid: "share-facebook" },
      { label: "Twitter/X", href: `https://twitter.com/intent/tweet?text=${msg}`, testid: "share-twitter" },
      { label: "Telegram", href: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(product?.name || "Zenith Flow product")}`, testid: "share-telegram" },
    ];
  };

  if (busy) {
    return <div className="p-8 text-white/60">Loading affiliate dashboard…</div>;
  }

  return (
    <div className="space-y-6" data-testid="affiliate-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
          <Sparkles size={12} /> Operator Affiliate Program
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-1">
          Share to Earn <span className="text-[#FBBF24]">2%</span>
        </h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">
          Every product in the shop is yours to promote. When someone buys through your link,
          you earn <strong className="text-[#FBBF24]">2% of their gross order value</strong>.
          Commissions unlock on delivery — no cap, no expiry, no minimums.
        </p>
      </div>

      {/* Personal code — always visible, sticky-worthy */}
      <div className="glass-card gold-border p-5" data-testid="affiliate-code-card">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-2">Your Affiliate Code</div>
        <div className="flex items-center gap-3 flex-wrap">
          <code className="text-2xl font-mono-num font-bold text-white bg-black/40 border border-white/10 rounded px-4 py-2 tracking-widest" data-testid="affiliate-my-code">
            {stats?.code}
          </code>
          <button
            onClick={() => copyToClipboard(stats?.code, "code")}
            className="text-xs px-3 py-2 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5 font-bold"
            data-testid="copy-code-btn"
          >
            {copiedField === "code" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy code</>}
          </button>
        </div>
        <div className="text-[11px] text-white/50 mt-2">
          Attribution: <strong>last-touch</strong>, {stats?.cookie_ttl_days || 7}-day window. Commissions credit your active balance on order delivery.
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={MousePointerClick} label="Clicks" value={stats?.total_clicks || 0} testid="kpi-clicks" />
        <KpiTile icon={ShoppingCart} label="Orders" value={stats?.total_orders || 0} testid="kpi-orders" />
        <KpiTile icon={Wallet} label="Earned (BC)" value={`₱${formatBC(stats?.earned_bc || 0)}`} accent testid="kpi-earned" />
        <KpiTile icon={TrendingUp} label="Pending (BC)" value={`₱${formatBC(stats?.pending_bc || 0)}`} testid="kpi-pending" />
      </div>

      {/* Product picker + link builder */}
      <div className="glass-card p-5" data-testid="affiliate-link-builder">
        <div className="flex items-center gap-2 mb-4">
          <Share2 size={16} className="text-[#FBBF24]" />
          <h2 className="font-display text-lg font-bold">Build a Share Link</h2>
        </div>
        {products.length === 0 ? (
          <div className="text-white/50 text-sm py-6 text-center" data-testid="no-products">
            No products in the shop yet. Ask your admin to stock the shelves — you&apos;ll be the first to promote them.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4 max-h-64 overflow-y-auto pr-1">
              {products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedProduct(p)}
                  className={`text-left p-2 rounded border transition-colors ${
                    selectedProduct?.id === p.id
                      ? "border-[#FBBF24] bg-[#FBBF24]/[0.08]"
                      : "border-white/10 hover:border-[#FBBF24]/40 bg-white/[0.02]"
                  }`}
                  data-testid={`affiliate-pick-product-${p.id}`}
                >
                  <div className="text-sm font-bold truncate">{p.name}</div>
                  <div className="text-[10px] text-white/50 flex items-center gap-1 mt-0.5">
                    <Package size={9} /> ₱{formatBC(p.price_bc)} · {p.category_label}
                  </div>
                </button>
              ))}
            </div>
            {selectedProduct && (
              <ShareLinkPanel
                product={selectedProduct}
                link={buildLink(selectedProduct.id)}
                copiedField={copiedField}
                onCopy={copyToClipboard}
                shareTargets={shareTargets(buildLink(selectedProduct.id), selectedProduct)}
              />
            )}
          </>
        )}
      </div>

      {/* Per-product breakdown */}
      <div className="glass-card p-5" data-testid="affiliate-per-product">
        <h2 className="font-display text-lg font-bold mb-3">Top Earners</h2>
        {(stats?.per_product || []).length === 0 ? (
          <div className="text-white/50 text-sm text-center py-6">
            No sales yet. Share a link to get started — you&apos;ll see your top-performing products here.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="affiliate-earnings-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24] border-b border-white/10">
                <th className="text-left py-2">Product</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Pending</th>
                <th className="text-right">Earned</th>
              </tr>
            </thead>
            <tbody>
              {stats.per_product.map((r) => (
                <tr key={r.product_id} className="border-b border-white/5" data-testid={`per-product-row-${r.product_id}`}>
                  <td className="py-2 truncate max-w-[300px]">{r.product_name}</td>
                  <td className="text-right font-mono-num">{r.orders}</td>
                  <td className="text-right font-mono-num text-white/60">₱{formatBC(r.pending_bc)}</td>
                  <td className="text-right font-mono-num text-[#FBBF24] font-bold">₱{formatBC(r.earned_bc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Legal note */}
      <div className="text-[11px] text-white/40 leading-relaxed max-w-3xl">
        Commissions are computed from the gross order value (product × qty − promo − coupon + shipping) and paid from ZFD&apos;s operating margin — not deducted from the shopper&apos;s payment. Cancelled or refunded orders reverse the commission via the ledger.
      </div>
    </div>
  );
}


function KpiTile({ icon: Icon, label, value, accent = false, testid }) {
  return (
    <div className="glass-card p-4" data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-[#FBBF24]" : "text-white/50"}`}>
        <Icon size={11} className="inline mr-1 -mt-0.5" /> {label}
      </div>
      <div className={`font-mono-num font-black text-2xl mt-1 ${accent ? "text-[#FBBF24]" : "text-white"}`}>{value}</div>
    </div>
  );
}


function ShareLinkPanel({ product, link, copiedField, onCopy, shareTargets }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(link)}&color=ffffff&bgcolor=0F2042`;
  return (
    <div className="border border-[#FBBF24]/30 bg-[#FBBF24]/[0.03] rounded-lg p-4 space-y-3" data-testid="share-link-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold">Share link for</div>
          <div className="font-bold text-white truncate" data-testid="share-product-name">{product.name}</div>
        </div>
        <Link to={`/p/${product.id}?ref=preview`} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 inline-flex items-center gap-1 text-white/75" data-testid="share-preview-link">
          <ExternalLink size={11} /> Preview page
        </Link>
      </div>

      <div className="flex items-stretch gap-2">
        <input
          readOnly
          value={link}
          onFocus={(e) => e.target.select()}
          className="zfd-input flex-1 font-mono-num text-xs"
          data-testid="share-link-input"
        />
        <button
          onClick={() => onCopy(link, `link-${product.id}`)}
          className="px-4 py-2 rounded bg-[#FBBF24] text-[#0F2042] font-bold text-xs uppercase tracking-widest hover:bg-yellow-300 inline-flex items-center gap-1.5"
          data-testid="share-copy-link"
        >
          {copiedField === `link-${product.id}` ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
        </button>
      </div>

      <div className="flex items-start gap-4 flex-wrap">
        <img src={qrSrc} alt="QR" className="w-32 h-32 rounded border border-white/10" data-testid="share-qr" />
        <div className="flex-1 min-w-[220px] space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold">Share directly</div>
          <div className="flex flex-wrap gap-2">
            {shareTargets.map((t) => (
              <a
                key={t.label}
                href={t.href}
                target="_blank"
                rel="noreferrer"
                className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1 hover:border-[#FBBF24]/40"
                data-testid={t.testid}
              >
                <Share2 size={11} /> {t.label}
              </a>
            ))}
          </div>
          <div className="text-[10px] text-white/45 mt-2">
            You&apos;ll earn <span className="text-[#FBBF24] font-bold">₱{formatBC(product.price_bc * 0.02)}</span> per single-item order on this SKU. More items in the cart = more commission.
          </div>
        </div>
      </div>
    </div>
  );
}
